import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import { BorderColor, Info } from '@mui/icons-material';
import AlertDialog from '../../../../../../../../components/Dialog/AlertDialog';
import RescheduleConfirmation from '../../../../../../../../components/Dialog/RescheduleConfirmation';
import SingleDayEvent from './SingleDayEvent';
import ContinuousDayEvent from './ContinuousDayEvent';
import MultipleDateEvent from './MultipleDateEvent';
import ShowDoneIconOnSelect from '../../../../../../../../icons/ShowDoneIconOnSelect';
import useContinuousDayEvent from '../../../hooks/useContinuosDayEvent';
import useSingleDayEvent from '../../../hooks/useSingleDayEvent';
import useMultipleDateEvent from '../../../hooks/useMultipleDateEvent';
import useEventSchedule from '../../../hooks/useEventSchedule';
import { previousTab } from '../../../../../../../../app/store/slices/event/event';
import { EVENT_TYPES } from '../../../../../../../../hooks/event-hooks/useEventStatus';
import { H6, Body2 } from '../../../../../../../../themes/typography';
import useEvent from '../../../../../../../../hooks/event-hooks/useEvent';
import '../../../../../../../../styles/components/EventSchedule.css';

export default function EventSchedule() {

    const dispatch = useDispatch();

    const {
        isAnyTicketSold
    } = useEvent();

    const {
        shouldShowEditButton,
        isEditModeOn,
        openRescheduleDialog,
        handleEventReschedule,
        handleOnConfirm,
        handleOnCancel,
        ticketLoading,
        eventTypeTooltips,
        selectedEventType,
        setSelectedEventType,
        hoveredEventType,
        setHoveredEventType
    } = useEventSchedule();

    const {
        continuosFormik,
        loading: continuosLoading
    } = useContinuousDayEvent();

    const {
        singleDayEventFormik,
        expanded,
        handleExpandChange,
        addSlot,
        deleteSlot,
        handleSlotDurationChange,
        slotDuration,
        slotDurationOptions,
        hour,
        setHour,
        handleCustomDurationChange,
        customDuration,
        durationError,
        loading: singleDayLoading
    } = useSingleDayEvent();

    const {
        multiDateEventFormik,
        handleDateSelection,
        handleDateDeletion,
        handleCloseDatePicker,
        isDatePickerClosed,
        expanded: multiDateExpanded,
        toggleAccordion,
        addSlotForDate,
        deleteSlotForDate,
        handleSlotDurationChange: handleSlotDurationChangeForDate,
        slotDurations,
        handleCustomDurationChange: handleCustomDurationChangeForDate,
        customSlotDurations,
        timeUnits,
        setTimeUnits,
        durationErrors,
        loading: multiDateLoading
    } = useMultipleDateEvent();

    return (
        <React.Fragment>
            <div className="event-schedule-details-form">
                <div className="event-schedule-details-form-container">
                    {(shouldShowEditButton && !isAnyTicketSold) && (
                        <div style={editBtnStyle}>
                            <Button
                                startIcon={<BorderColor />}
                                variant="text"
                                onClick={handleEventReschedule}
                                disabled={isEditModeOn}
                            >
                                Edit
                            </Button>
                        </div>
                    )}

                    <div className="event-schedule-details-form-starts">
                        {/* Event Schedule Type */}
                        <div className="event-schedule-type-selection">
                            <div className="schedule-selection-title">
                                <H6>Select Event Type *</H6>
                            </div>
                            <ToggleButtonGroup
                                color="main"
                                value={selectedEventType}
                                exclusive
                                onChange={(event, newValue) => setSelectedEventType(newValue)}
                                aria-label="Event Type"
                                disabled={!isEditModeOn}
                            >
                                <ToggleButton
                                    value={EVENT_TYPES.singleDay}
                                    onMouseEnter={() => setHoveredEventType(EVENT_TYPES.singleDay)}
                                    onMouseLeave={() => setHoveredEventType(null)}
                                >
                                    Single Date
                                    {selectedEventType === EVENT_TYPES.singleDay && (
                                        <ShowDoneIconOnSelect />
                                    )}
                                </ToggleButton>
                                <ToggleButton
                                    value={EVENT_TYPES.multiDay}
                                    onMouseEnter={() => setHoveredEventType(EVENT_TYPES.multiDay)}
                                    onMouseLeave={() => setHoveredEventType(null)}
                                >
                                    Multi Date - Repeating
                                    {selectedEventType === EVENT_TYPES.multiDay && (
                                        <ShowDoneIconOnSelect />
                                    )}
                                </ToggleButton>
                                <ToggleButton
                                    value={EVENT_TYPES.continuous}
                                    onMouseEnter={() => setHoveredEventType(EVENT_TYPES.continuous)}
                                    onMouseLeave={() => setHoveredEventType(null)}
                                >
                                    Multi Date - Continous
                                    {selectedEventType === EVENT_TYPES.continuous && (
                                        <ShowDoneIconOnSelect />
                                    )}
                                </ToggleButton>
                            </ToggleButtonGroup>
                            <div className="event-type-info-tooltip">
                                <Info color="white" />
                                <Body2>
                                    {eventTypeTooltips[hoveredEventType || selectedEventType]}
                                </Body2>
                            </div>
                        </div>

                        {selectedEventType === EVENT_TYPES.singleDay && (
                            <SingleDayEvent
                                isEditModeOn={isEditModeOn}
                                addSlot={addSlot}
                                expanded={expanded}
                                isHourSelected={hour}
                                deleteSlot={deleteSlot}
                                setIsHourSelected={setHour}
                                durationError={durationError}
                                formik={singleDayEventFormik}
                                options={slotDurationOptions}
                                slotOptionValue={slotDuration}
                                customDurationValue={customDuration}
                                handleExpandChange={handleExpandChange}
                                onSlotOptionChange={handleSlotDurationChange}
                                handleCustomDurationChange={handleCustomDurationChange}
                            />
                        )}

                        {selectedEventType === EVENT_TYPES.multiDay && (
                            // <MultiDayEvent
                            //     formik={multiDateEventFormik}
                            //     handleDateSelection={handleDateSelection}
                            //     handleDateDeletion={handleDateDeletion}
                            //     handleCloseDatePicker={handleCloseDatePicker}
                            //     isDatePickerClosed={isDatePickerClosed}
                            //     multiSlotRef={recurringEventSlotRef}
                            // />
                            <MultipleDateEvent
                                isEditModeOn={isEditModeOn}
                                formik={multiDateEventFormik}
                                handleDateSelection={handleDateSelection}
                                handleDateDeletion={handleDateDeletion}
                                handleCloseDatePicker={handleCloseDatePicker}
                                isDatePickerClosed={isDatePickerClosed}
                                expanded={multiDateExpanded}
                                toggleAccordion={toggleAccordion}
                                addSlotForDate={addSlotForDate}
                                deleteSlotForDate={deleteSlotForDate}
                                handleSlotDurationChange={handleSlotDurationChangeForDate}
                                slotDurations={slotDurations}
                                handleCustomDurationChange={handleCustomDurationChangeForDate}
                                customSlotDurations={customSlotDurations}
                                timeUnits={timeUnits}
                                setTimeUnits={setTimeUnits}
                                durationErrors={durationErrors}
                            />
                        )}

                        {selectedEventType === EVENT_TYPES.continuous && (
                            <ContinuousDayEvent
                                isEditModeOn={isEditModeOn}
                                formik={continuosFormik}
                            />
                        )}
                    </div>
                </div>
                <div className="submit-button-container">
                    <Button variant="text" onClick={() => dispatch(previousTab())}>
                        Back to Basic Details
                    </Button>
                    <Button
                        variant="main"
                        disabled={continuosLoading || singleDayLoading || multiDateLoading}
                        onClick={() => {
                            if (selectedEventType === EVENT_TYPES.continuous) {
                                continuosFormik.handleSubmit();
                            } else if (selectedEventType === EVENT_TYPES.singleDay) {
                                singleDayEventFormik.handleSubmit();
                            } else if (selectedEventType === EVENT_TYPES.multiDay) {
                                multiDateEventFormik.handleSubmit();
                            }
                        }}
                    >
                        Next{' '}
                        {(continuosLoading || singleDayLoading || multiDateLoading) && (
                            <CircularProgress
                                size={20}
                                sx={{ marginLeft: '0.75rem', color: 'var(--primary-color)' }}
                            />
                        )}
                    </Button>
                </div>
            </div>

            {/* Alert Dialog to ask user to reschedule the event */}
            <AlertDialog
                open={openRescheduleDialog}
                dialogTitle="Reschedule Confirmation"
                dialogId="reschedule-confirmation"
                primaryBtnName="Confirm"
                secondaryBtnName="Cancel"
                onConfirm={handleOnConfirm}
                onCancel={handleOnCancel}
                disabled={ticketLoading}
                loading={ticketLoading}
            >
                <RescheduleConfirmation />
            </AlertDialog>
        </React.Fragment>
    );
}

const editBtnStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    paddingBottom: '0.5rem'
};
